import React from "react";
import { Box, Typography, useTheme, Tooltip } from "@mui/material";

const HorizontalCapsuleBarChart = ({
  labels = [],
  values = [],
  colors = [],
  mainColor = "#8e44ad",
  barHeight = 36,
  maxBarWidth = "100%",
  showPercentage = true,
  loading = false,
}) => {
  const theme = useTheme();

  // Determine the maximum value to calculate relative widths
  const maxValue = Math.max(...values, 1);
  const totalValue = values.reduce((a, b) => Number(a) + Number(b), 0);

  // Purple gradient palette mapping
  const purplePalette = [
    "#d7aefb", // Lightest
    "#c084fc",
    "#a855f7",
    "#9333ea",
    "#7e22ce",
    "#6b21a8",
    "#3b0764", // Darkest
  ];
  
  if(totalValue === 0 && !loading) {
       return (
           <Box sx={{ width: "100%", mt: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "150px" }}>
               <Typography variant="h6" color="text.secondary">
                   No Data Found
               </Typography>
           </Box>
       )
  }

  return (
    <Box sx={{ width: "100%", mt: 1, display: "flex", gap: 2 }}>
        {/* Rotated Y-Axis Label */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "20px" }}>
             <Typography 
                sx={{ 
                    writingMode: "vertical-rl", 
                    transform: "rotate(180deg)",
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.85rem",
                    letterSpacing: "1px"
                }}
             >
                Lead Statuses
             </Typography>
        </Box>

        {/* Chart Area */}
        <Box sx={{ flexGrow: 1 }}>
            {labels.map((label, index) => {
                const value = values[index] || 0;
                const widthPercentage = (value / maxValue) * 100;
                const totalPercentage = totalValue > 0 ? ((value / totalValue) * 100).toFixed(1) : 0;
                
                // Map the current index to a palette index to ensure a smooth gradient from lightest to darkest
                const paletteIndex = labels.length > 1 
                    ? Math.floor((index / (labels.length - 1)) * (purplePalette.length - 1)) 
                    : 0;
                const barColor = colors[index] || purplePalette[paletteIndex];

                return (
                <Box key={index}>
                    <Box
                        sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 0.5,
                        width: "100%",
                        }}
                    >
                        {/* Label */}
                        <Box sx={{ width: "20%", minWidth: "120px", pr: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.85rem"
                            }}
                        >
                            {label}
                        </Typography>
                        </Box>

                        {/* Bar Area */}
                        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", mr: 2 }}>
                            <Tooltip title={`Count: ${value}`} arrow placement="top">
                                <Box
                                    sx={{
                                    width: `${widthPercentage}%`,
                                    height: barHeight,
                                    bgcolor: barColor,
                                    borderRadius: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    px: 1,
                                    transition: "width 0.5s ease-in-out",
                                    minWidth: "65px",
                                    position: "relative",
                                    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                                    background: `linear-gradient(90deg, ${barColor} 0%, ${barColor} 100%)` // Simplified gradient for cleaner look or match purplePalette
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: "#fff",
                                            borderRadius: "12px",
                                            px: 1,
                                            py: 0.2,
                                            minWidth: "45px",
                                            width: "fit-content",
                                            textAlign: "center",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "text.primary", // Text color matches bar
                                                fontWeight: "bold",
                                                fontSize: "0.7rem"
                                            }}
                                        >
                                            {totalPercentage}%
                                        </Typography>
                                    </Box>
                                </Box>
                            </Tooltip>
                        </Box>

                        {/* Percentage */}
                        {/* {showPercentage && (
                            <Box sx={{ width: "2%", minWidth: "30px", textAlign: "center", marginRight: "20px" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "text.secondary",
                                        fontSize: "0.9rem"
                                    }}
                                >
                                    {totalPercentage === 0 && value > 0 ? "< 1%" : `${totalPercentage}%`}
                                </Typography>
                            </Box>
                        )} */}
                    </Box>
                    {/* Separator Line */}
                    <Box sx={{ width: "100%", height: "1px", bgcolor: "divider" }} />
                </Box>
                );
            })}
             {/* X-Axis / Footer */}
             <Box sx={{ mt: 0.5, pr: 0 }}>
                 {/* Ticks and Labels */}
                 <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pl: "20%", pr: "8%" }}>
                     {[0, 0.2, 0.4, 0.6, 0.8, 1].map((tick) => { // Fixed 5 intervals
                         const tickValue = Math.round(maxValue * tick);
                         return (
                             <Box key={tick} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "20px" }}>
                                 <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                                     {tickValue}
                                 </Typography>
                             </Box>
                         )
                     })}
                 </Box>
                 <Box sx={{  display: "flex", justifyContent: "center", mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        Number of leads
                    </Typography>
                 </Box>
            </Box>

            {loading && (
                <Typography>Loading...</Typography>
            )}
        </Box>
    </Box>
  );
};

export default HorizontalCapsuleBarChart;
